import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Camera, CarFront, ChevronDown, ChevronUp, ClipboardList, FileText, LogIn, LogOut, Loader2, MapPinned, Package, Plus, Scale, Search, SlidersHorizontal, Trash2, UploadCloud, X, ZoomIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.bootstrap5.css';
import './SecurityPage.css';
import LocationMap from './components/LocationMap';
import { resolveSlipMediaUrl } from './utils/mediaUrl';
import { cacheHasData, readCache, updateCacheIfChanged } from './utils/localCache';

const STORAGE_KEY = 'security_activity_logs';
const SECURITY_API = 'https://jne-cilacap.com/slip/public/api/security';
const emptyVehicle = {
  direction: 'Masuk', jenis: '', kode: '', nopol: '', driver: '',
  active: '1', keterangan: '', titipan: [], surat_jalan: [], memo: '', photo: null,
};
const emptyPatrol = { round: '1', location: '', condition: 'Aman', findings: '', action: '', latitude: '', longitude: '', accuracy: '', photo: null };
const PATROL_POINTS = ['Office', 'Toilet Office', 'Parkiran SCO', 'SCO Atas', 'SCO Bawah', 'Toilet SCO', 'GA', 'Depan GA', 'Lorong samping', 'Packing kayu', 'Parkir Truk', 'Parkir Mobil', 'Parkir Motor', 'Taman', 'Kandang Hewan', 'R tunggu Sopir', 'Ruang Admin', 'Masjid Alfalah', 'Toilet Masjid', 'Undel', 'Delivery', 'Inbound', 'Transit', 'Outbound', 'Toilet Security'];
const INVENTORY_ITEMS = ['Telepon', 'Komputer', 'Dispenser', 'Kipas angin', 'Galon', 'Stick lamp', 'Tongkat T', 'Meja', 'Kursi', 'Kunci gedung', 'Kunci armada'];
const INVENTORY_OPTIONS_KEY = 'security_custom_inventory_items';
const SECURITY_VEHICLE_CACHE = 'security_vehicle_server_v2';
const SECURITY_PATROL_CACHE = 'security_patrol_server_v1';
const SECURITY_DEPOSIT_CACHE = 'security_deposits_v1';
const SECURITY_ACTIVITY_CACHE = 'security_activity_feed_v1';
const toLocalDateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const currentPatrolShift = () => {
  const now = new Date();
  const minutes = (now.getHours() * 60) + now.getMinutes();
  if (minutes < 7 * 60) {
    const workDate = new Date(now); workDate.setDate(workDate.getDate() - 1);
    return { number: '2', date: toLocalDateKey(workDate) };
  }
  return { number: minutes < 19 * 60 ? '1' : '2', date: toLocalDateKey(now) };
};

const readSelectOptions = (storageKey) => {
  try {
    const values = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(values) ? values.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const SearchCreateSelect = ({ storageKey, value, onChange, placeholder, maxLength, uppercase = false }) => {
  const selectRef = useRef(null);
  const instanceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const savedOptions = readSelectOptions(storageKey);
    const commitSelection = (nextValue) => {
      const cleanValue = String(nextValue || '').trim();
      if (cleanValue) {
        const latest = readSelectOptions(storageKey);
        if (!latest.some(item => item.toLowerCase() === cleanValue.toLowerCase())) {
          localStorage.setItem(storageKey, JSON.stringify([...latest, cleanValue]));
        }
      }
      onChangeRef.current(cleanValue);
    };
    instanceRef.current = new TomSelect(selectRef.current, {
      options: savedOptions.map(item => ({ value: item, text: item })),
      valueField: 'value',
      labelField: 'text',
      searchField: ['text'],
      maxItems: 1,
      persist: false,
      createOnBlur: true,
      closeAfterSelect: true,
      selectOnTab: true,
      create(input) {
        const cleanValue = (uppercase ? input.toUpperCase() : input).trim().slice(0, maxLength);
        return cleanValue ? { value: cleanValue, text: cleanValue } : false;
      },
      placeholder,
      onItemAdd(nextValue) { commitSelection(nextValue); },
      onChange(nextValue) { commitSelection(nextValue); },
    });
    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [storageKey, placeholder, maxLength, uppercase]);

  useEffect(() => {
    if (!instanceRef.current) return;
    if (value && !instanceRef.current.options[value]) {
      instanceRef.current.addOption({ value, text: value });
    }
    instanceRef.current.setValue(value || '', true);
  }, [value]);

  return <div className="security-tom-select"><select ref={selectRef} aria-label={placeholder} /></div>;
};

const TABS = [
  { key: 'vehicle', label: 'Keluar/Masuk', icon: CarFront },
  { key: 'patrol', label: 'Patroli', icon: MapPinned },
  { key: 'activity', label: 'Kegiatan', icon: ClipboardList },
];

const readLogs = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const resolveSuratJalanDirection = (suratJalan = {}, fallbackDirection = 'Masuk') => {
  const rawValue = String(suratJalan?.direction ?? '').trim();
  if (!rawValue) return fallbackDirection;
  const normalized = rawValue.toLowerCase();
  if (normalized === 'masuk' || normalized === 'in') return 'Masuk';
  if (normalized === 'keluar' || normalized === 'out') return 'Keluar';
  return fallbackDirection;
};

export const resolveCompletionSuratJalanDirection = (action = 'masuk') => {
  const normalized = String(action || 'masuk').trim().toLowerCase();
  return normalized === 'keluar' ? 'Keluar' : 'Masuk';
};

const inputStyle = {
  width: '100%', padding: '11px 12px', boxSizing: 'border-box',
  border: '1.5px solid #dbe2ea', borderRadius: 11, outline: 'none',
  fontSize: 13, background: '#fff',
};

const Field = ({ label, children, style }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569', ...style }}>
    {label}
    {children}
  </label>
);

const SignaturePad = ({ label, signer, value, onChange }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * (canvas.width / rect.width), y: (event.clientY - rect.top) * (canvas.height / rect.height) };
  };
  const startDrawing = (event) => {
    const canvas = canvasRef.current; const context = canvas.getContext('2d'); const point = pointFromEvent(event);
    drawingRef.current = true; canvas.setPointerCapture(event.pointerId); context.beginPath(); context.moveTo(point.x, point.y);
  };
  const draw = (event) => {
    if (!drawingRef.current) return;
    const context = canvasRef.current.getContext('2d'); const point = pointFromEvent(event);
    context.lineTo(point.x, point.y); context.stroke();
  };
  const finishDrawing = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false; onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current; canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height); onChange('');
  };
  useEffect(() => {
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    context.lineWidth = 3; context.lineCap = 'round'; context.lineJoin = 'round'; context.strokeStyle = '#0f172a';
  }, []);

  return <div style={{ padding: 12, border: '1px solid #dbe2ea', borderRadius: 13, background: '#f8fafc' }}>
    <div style={{ marginBottom: 3, color: '#475569', fontSize: 11, fontWeight: 900 }}>{label}</div>
    <div style={{ marginBottom: 8, color: '#64748b', fontSize: 10 }}>{signer}</div>
    <canvas ref={canvasRef} width={600} height={180} onPointerDown={startDrawing} onPointerMove={draw} onPointerUp={finishDrawing} onPointerCancel={finishDrawing} style={{ display: 'block', width: '100%', height: 120, border: `1.5px dashed ${value ? '#34d399' : '#94a3b8'}`, borderRadius: 10, background: '#fff', touchAction: 'none', cursor: 'crosshair' }} />
    <button type="button" onClick={clear} style={{ marginTop: 7, padding: 0, border: 0, background: 'transparent', color: '#dc2626', fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>Hapus tanda tangan</button>
  </div>;
};

const getUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    return {
      id: user.id,
      name: `${user.nama_depan || ''} ${user.nama_belakang || ''}`.trim() || user.nama || 'Security',
      position: user.jabatan || 'Security',
    };
  } catch {
    return { id: '', name: 'Security' };
  }
};

const normalizeOfficerName = (name) => String(name || '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('id-ID');

export default function SecurityPage() {
  const navigate = useNavigate();
  const currentUser = useMemo(getUser, []);
  const [activeTab, setActiveTab] = useState('vehicle');
  const [vehicleFilter, setVehicleFilter] = useState('all'); // 'all' | 'incomplete' | 'complete'
  const activePatrolShift = useMemo(currentPatrolShift, []);
  const [patrolViewRound, setPatrolViewRound] = useState('1');
  const [patrolViewDate, setPatrolViewDate] = useState(activePatrolShift.date);
  const [patrolViewShift, setPatrolViewShift] = useState(activePatrolShift.number);
  const [showPatrolFilters, setShowPatrolFilters] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [logs, setLogs] = useState(readLogs);
  const [vehicle, setVehicle] = useState(emptyVehicle);
  const [preparedDeposits, setPreparedDeposits] = useState(() => readCache(SECURITY_DEPOSIT_CACHE, []));
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(() => !cacheHasData(SECURITY_DEPOSIT_CACHE));
  const [photoPreview, setPhotoPreview] = useState('');
  const [isSavingVehicle, setIsSavingVehicle] = useState(false);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(() => !cacheHasData(SECURITY_VEHICLE_CACHE));
  const photoInputRef = useRef(null);
  const patrolPhotoRef = useRef(null);
  const [completion, setCompletion] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [suratJalanModal, setSuratJalanModal] = useState(null);
  const [isSavingSuratJalan, setIsSavingSuratJalan] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [patrol, setPatrol] = useState(emptyPatrol);
  const [patrolPhotoPreview, setPatrolPhotoPreview] = useState('');
  const [isSavingPatrol, setIsSavingPatrol] = useState(false);
  const [activityFeed, setActivityFeed] = useState(() => readCache(SECURITY_ACTIVITY_CACHE, []));
  const [handover, setHandover] = useState({ receiver: '', condition: 'Lengkap dan baik', notes: '', items: INVENTORY_ITEMS });
  const [inventoryOptions, setInventoryOptions] = useState(() => [...new Set([...INVENTORY_ITEMS, ...readSelectOptions(INVENTORY_OPTIONS_KEY)])]);
  const [newInventoryItem, setNewInventoryItem] = useState('');
  const [giverSignature, setGiverSignature] = useState('');
  const [receiverSignature, setReceiverSignature] = useState('');
  const [isSavingHandover, setIsSavingHandover] = useState(false);

  useEffect(() => {
    if (showAddForm || completion || suratJalanModal) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [showAddForm, completion, suratJalanModal]);

  const visibleActivityFeed = useMemo(() => {
    const currentOfficerName = normalizeOfficerName(currentUser.name);
    return activityFeed.filter(item => normalizeOfficerName(item.petugas || item.giver) === currentOfficerName);
  }, [activityFeed, currentUser.name]);

  const vehicleStats = useMemo(() => {
    const vLogs = logs.filter(log => log.type === 'vehicle');
    const complete = vLogs.filter(log => Boolean(log.masuk && log.keluar)).length;
    const incomplete = vLogs.length - complete;
    return { total: vLogs.length, complete, incomplete };
  }, [logs]);

  const addSuratJalan = () => {
    setVehicle(current => ({
      ...current,
      surat_jalan: [
        ...(current.surat_jalan || []),
        { id: Date.now() + Math.random(), no_surat_jalan: '', tonase: '', qty: '', keterangan: '', direction: current.direction },
      ],
    }));
  };

  const updateSuratJalan = (index, field, value) => {
    setVehicle(current => {
      const list = [...(current.surat_jalan || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, surat_jalan: list };
    });
  };

  const removeSuratJalan = (index) => {
    setVehicle(current => {
      const list = (current.surat_jalan || []).filter((_, i) => i !== index);
      return { ...current, surat_jalan: list };
    });
  };

  const visibleLogs = useMemo(() => {
    const keyword = historySearch.trim().toLocaleLowerCase('id-ID');
    return logs.filter(log => {
      if (log.type !== activeTab) return false;
      if (activeTab === 'vehicle') {
        const isComplete = Boolean(log.masuk && log.keluar);
        if (vehicleFilter === 'incomplete' && isComplete) return false;
        if (vehicleFilter === 'complete' && !isComplete) return false;
      }
      if (!keyword) return true;
      const sjTexts = (log.surat_jalan || log.suratJalan || []).map(sj => `${sj.no_surat_jalan} ${sj.tonase} ${sj.qty} ${resolveSuratJalanDirection(sj, log.direction || 'Masuk')} ${sj.keterangan || ''}`).join(' ');
      const statusText = log.masuk && log.keluar ? 'lengkap selesai' : (log.masuk ? 'masuk belum keluar' : 'keluar belum masuk');
      const searchableText = [
        log.direction, log.jenis, log.kode, log.nopol, log.plate, log.driver,
        log.petugas, log.officer_name, log.keterangan, log.titipan, log.memo,
        log.masuk, log.keluar, log.tanggal, log.location, log.condition,
        log.findings, log.activity_type, log.description, log.condition, log.notes,
        sjTexts, statusText,
      ].filter(Boolean).join(' ').toLocaleLowerCase('id-ID');
      return searchableText.includes(keyword);
    });
  }, [logs, activeTab, historySearch, vehicleFilter]);

  const updateLogsWithCache = updater => setLogs(current => {
    const updated = typeof updater === 'function' ? updater(current) : updater;
    updateCacheIfChanged(STORAGE_KEY, updated);
    updateCacheIfChanged(SECURITY_VEHICLE_CACHE, updated.filter(log => log.type === 'vehicle'));
    updateCacheIfChanged(SECURITY_PATROL_CACHE, updated.filter(log => log.type === 'patrol'));
    return updated;
  });

  useEffect(() => {
    let cancelled = false;
    fetch(SECURITY_API, { headers: { Accept: 'application/json' } })
      .then(async response => {
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Gagal mengambil data security.');
        return Array.isArray(result.data) ? result.data : [];
      })
      .then(rows => {
        if (cancelled) return;
        const serverLogs = rows.map(row => ({
          ...row,
          type: 'vehicle',
          direction: row.keluar && !row.masuk ? 'Keluar' : 'Masuk',
          plate: row.nopol,
          officer_name: row.petugas,
          timestamp: row.created_at || row.tanggal,
          surat_jalan: (row.surat_jalan || row.suratJalan || []).map(sj => ({
            ...sj,
            direction: resolveSuratJalanDirection(sj, row.keluar && !row.masuk ? 'Keluar' : 'Masuk'),
          })),
        }));
        if (!updateCacheIfChanged(SECURITY_VEHICLE_CACHE, serverLogs)) return;
        setLogs(current => {
          const updated = [...serverLogs, ...current.filter(log => log.type !== 'vehicle')];
          updateCacheIfChanged(STORAGE_KEY, updated);
          return updated;
        });
      })
      .catch(error => console.warn('Riwayat security memakai cache lokal:', error))
      .finally(() => { if (!cancelled) setIsLoadingVehicle(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${SECURITY_API}/deposits`, { headers: { Accept: 'application/json' } })
      .then(async response => { const result = await response.json(); if (!response.ok) throw new Error(result.message || 'Gagal mengambil daftar titipan.'); return result.data || []; })
      .then(rows => { if (!cancelled && updateCacheIfChanged(SECURITY_DEPOSIT_CACHE, rows)) setPreparedDeposits(rows); })
      .catch(error => console.warn('Daftar titipan ATK belum dapat dimuat:', error))
      .finally(() => { if (!cancelled) setIsLoadingDeposits(false); });
    return () => { cancelled = true; };
  }, []);

  const loadActivityFeed = () => fetch(`${SECURITY_API}/activity-summary`, { headers: { Accept: 'application/json' } })
    .then(async response => { const result = await response.json(); if (!response.ok) throw new Error(result.message); const rows = result.data || []; if (updateCacheIfChanged(SECURITY_ACTIVITY_CACHE, rows)) setActivityFeed(rows); })
    .catch(error => console.warn('Rangkuman kegiatan belum dapat dimuat:', error));

  useEffect(() => { loadActivityFeed(); }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${SECURITY_API}/patrols`, { headers: { Accept: 'application/json' } })
      .then(async response => { const result = await response.json(); if (!response.ok) throw new Error(result.message); return result.data || []; })
      .then(rows => {
        if (cancelled) return;
        const serverLogs = rows.map(row => ({ ...row, type: 'patrol', officer_name: row.petugas, timestamp: row.created_at }));
        if (!updateCacheIfChanged(SECURITY_PATROL_CACHE, serverLogs)) return;
        setLogs(current => {
          const updated = [...serverLogs, ...current.filter(log => log.type !== 'patrol')];
          updateCacheIfChanged(STORAGE_KEY, updated);
          return updated;
        });
      })
      .catch(error => console.warn('Riwayat patroli memakai cache lokal:', error));
    return () => { cancelled = true; };
  }, []);

  const saveLog = (payload) => {
    const user = getUser();
    const entry = {
      id: `SEC-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: activeTab,
      ...payload,
      officer_id: user.id,
      officer_name: user.name,
      timestamp: new Date().toISOString(),
    };
    const updated = [entry, ...logs];
    setLogs(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event('securityDataUpdated'));
    setShowAddForm(false);
    Swal.fire({ icon: 'success', title: 'Berhasil Dicatat', timer: 1300, showConfirmButton: false });
  };

  const submitVehicle = async (event) => {
    event.preventDefault();
    if (!vehicle.jenis.trim() || !vehicle.nopol.trim() || !vehicle.driver.trim() || !vehicle.photo) {
      Swal.fire('Data Belum Lengkap', 'Jenis, nomor polisi, nama pengemudi, dan foto wajib diisi.', 'warning');
      return;
    }
    const user = getUser();
    const now = new Date();
    const time = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false }).replace('.', '-');
    const formData = new FormData();
    const selectedDeposits = preparedDeposits.filter(item => vehicle.titipan.some(id => String(id) === String(item.id)));
    const atkDescription = selectedDeposits.map(item => `ATK ${item.agen || 'Agen belum diisi'}`).join(', ');
    const vehicleDescription = [vehicle.keterangan.trim(), atkDescription].filter(Boolean).join(' · ');
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const suratJalanRows = vehicle.surat_jalan || [];
    const hasIncompleteSuratJalan = suratJalanRows.some(sj => (
      !sj.no_surat_jalan?.trim() || sj.tonase === '' || sj.tonase === null || sj.qty === '' || sj.qty === null
    ));
    if (hasIncompleteSuratJalan) {
      Swal.fire('Surat Jalan Belum Lengkap', 'Setiap surat jalan wajib memiliki nomor, tonase (Kg), dan qty (Koli).', 'warning');
      return;
    }
    const validSuratJalan = suratJalanRows.map(sj => ({
      no_surat_jalan: sj.no_surat_jalan.trim(),
      tonase: Number(sj.tonase),
      qty: Number(sj.qty),
      keterangan: (sj.keterangan || '').trim(),
      direction: resolveSuratJalanDirection(sj, vehicle.direction),
    }));

    Object.entries({ jenis: vehicle.jenis, kode: vehicle.kode, nopol: vehicle.nopol, petugas: user.name, driver: vehicle.driver, active: vehicle.active, masuk: vehicle.direction === 'Masuk' ? time : '', keluar: vehicle.direction === 'Keluar' ? time : '', tanggal: localDate, keterangan: vehicleDescription, titipan: atkDescription, memo: vehicle.memo }).forEach(([key, value]) => formData.append(key, value));
    selectedDeposits.forEach(item => formData.append('penjualan_ids[]', item.id));
    if (validSuratJalan.length) {
      formData.append('surat_jalan', JSON.stringify(validSuratJalan));
    }
    formData.append('photo', vehicle.photo);
    setIsSavingVehicle(true);
    try {
      const response = await fetch(SECURITY_API, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Data gagal disimpan.');
      const row = result.data;
      const normalizedSuratJalan = (row.surat_jalan || row.suratJalan || validSuratJalan).map((sj) => ({
        ...sj,
        direction: resolveSuratJalanDirection(sj, vehicle.direction),
      }));
      updateLogsWithCache(current => [{ ...row, type: 'vehicle', direction: vehicle.direction, plate: row.nopol, officer_name: row.petugas, timestamp: row.created_at, surat_jalan: normalizedSuratJalan }, ...current]);
      setVehicle(emptyVehicle);
      if (selectedDeposits.length) { const selectedIds = new Set(selectedDeposits.map(item => String(item.id))); setPreparedDeposits(current => { const updated = current.filter(item => !selectedIds.has(String(item.id))); updateCacheIfChanged(SECURITY_DEPOSIT_CACHE, updated); return updated; }); }
      setPhotoPreview('');
      if (photoInputRef.current) photoInputRef.current.value = '';
      setShowAddForm(false);
      loadActivityFeed();
      Swal.fire({ icon: 'success', title: 'Tersimpan ke Database', timer: 1500, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Gagal Menyimpan', error.message || 'Periksa koneksi lalu coba lagi.', 'error');
    } finally {
      setIsSavingVehicle(false);
    }
  };

  const handlePhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) {
      Swal.fire('Foto Tidak Valid', 'Gunakan JPG/PNG/WEBP dengan ukuran maksimal 5 MB.', 'warning');
      event.target.value = '';
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setVehicle(current => ({ ...current, photo: file }));
    setPhotoPreview(URL.createObjectURL(file));
  };

  const addCompletionSuratJalan = () => {
    setCompletion(current => {
      if (!current) return current;
      const nextDirection = resolveCompletionSuratJalanDirection(current.action);
      return {
        ...current,
        surat_jalan: [
          ...(current.surat_jalan || []),
          { id: Date.now() + Math.random(), no_surat_jalan: '', tonase: '', qty: '', keterangan: '', direction: nextDirection },
        ],
      };
    });
  };

  const updateCompletionSuratJalan = (index, field, value) => {
    setCompletion(current => {
      if (!current) return current;
      const list = [...(current.surat_jalan || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, surat_jalan: list };
    });
  };

  const removeCompletionSuratJalan = (index) => {
    setCompletion(current => {
      if (!current) return current;
      const list = (current.surat_jalan || []).filter((_, i) => i !== index);
      return { ...current, surat_jalan: list };
    });
  };

  const openCompletion = (log) => {
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const action = log.masuk ? 'keluar' : 'masuk';
    setCompletion({
      log,
      action,
      keterangan: log.keterangan || '',
      time: currentTime,
      surat_jalan: [],
    });
  };

  const openPatrolPoint = (location) => {
    setPatrol({ ...emptyPatrol, round: patrolViewRound, location });
    setPatrolPhotoPreview('');
    setShowAddForm(true);
  };

  const closeCompletion = () => {
    setCompletion(null);
  };

  const submitCompletion = async (event) => {
    event.preventDefault();
    if (!completion) return;

    const suratJalanRows = completion.surat_jalan || [];
    const hasIncompleteSuratJalan = suratJalanRows.some(sj => (
      !sj.no_surat_jalan?.trim() || sj.tonase === '' || sj.tonase === null || sj.qty === '' || sj.qty === null
    ));
    if (suratJalanRows.length && hasIncompleteSuratJalan) {
      Swal.fire('Surat Jalan Belum Lengkap', 'Setiap surat jalan wajib memiliki nomor, tonase (Kg), dan qty (Koli).', 'warning');
      return;
    }

    const validSuratJalan = suratJalanRows.map(sj => ({
      no_surat_jalan: sj.no_surat_jalan.trim(),
      tonase: Number(sj.tonase),
      qty: Number(sj.qty),
      keterangan: (sj.keterangan || '').trim(),
      direction: resolveCompletionSuratJalanDirection(completion.action),
    }));

    const formData = new FormData();
    formData.append('action', completion.action);
    formData.append('time', completion.time.replace(':', '-'));
    formData.append('keterangan', completion.keterangan);
    if (validSuratJalan.length) {
      formData.append('surat_jalan', JSON.stringify(validSuratJalan));
    }
    setIsCompleting(true);
    try {
      const response = await fetch(`${SECURITY_API}/${completion.log.id}/complete`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Data gagal dilengkapi.');
      const updatedRow = result.data;
      const normalizedSuratJalan = (updatedRow.surat_jalan || updatedRow.suratJalan || validSuratJalan).map((sj) => ({
        ...sj,
        direction: resolveSuratJalanDirection(sj, resolveCompletionSuratJalanDirection(completion.action)),
      }));
      updateLogsWithCache(current => current.map(log => log.id === completion.log.id ? {
        ...log,
        ...updatedRow,
        type: 'vehicle',
        direction: updatedRow.keluar && !updatedRow.masuk ? 'Keluar' : 'Masuk',
        plate: updatedRow.nopol,
        officer_name: updatedRow.petugas,
        timestamp: updatedRow.created_at || log.timestamp,
        surat_jalan: normalizedSuratJalan,
      } : log));
      loadActivityFeed();
      closeCompletion();
      Swal.fire({ icon: 'success', title: 'Data Sudah Lengkap', timer: 1500, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Gagal Melengkapi', error.message || 'Periksa koneksi lalu coba lagi.', 'error');
    } finally {
      setIsCompleting(false);
    }
  };

  const openSuratJalanModal = (log) => {
    const defaultDirection = log.masuk && !log.keluar ? 'Masuk' : (log.keluar && !log.masuk ? 'Keluar' : 'Masuk');
    setSuratJalanModal({
      log,
      surat_jalan: [
        { id: Date.now() + Math.random(), no_surat_jalan: '', tonase: '', qty: '', keterangan: '', direction: defaultDirection },
      ],
    });
  };

  const closeSuratJalanModal = () => setSuratJalanModal(null);

  const addModalSuratJalanRow = () => {
    setSuratJalanModal(current => {
      if (!current) return current;
      const defaultDirection = current.log.masuk && !current.log.keluar ? 'Masuk' : (current.log.keluar && !current.log.masuk ? 'Keluar' : 'Masuk');
      return {
        ...current,
        surat_jalan: [
          ...(current.surat_jalan || []),
          { id: Date.now() + Math.random(), no_surat_jalan: '', tonase: '', qty: '', keterangan: '', direction: defaultDirection },
        ],
      };
    });
  };

  const updateModalSuratJalanRow = (index, field, value) => {
    setSuratJalanModal(current => {
      if (!current) return current;
      const list = [...(current.surat_jalan || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, surat_jalan: list };
    });
  };

  const removeModalSuratJalanRow = (index) => {
    setSuratJalanModal(current => {
      if (!current) return current;
      const list = (current.surat_jalan || []).filter((_, i) => i !== index);
      return { ...current, surat_jalan: list };
    });
  };

  const submitSuratJalanModal = async (event) => {
    event.preventDefault();
    if (!suratJalanModal) return;
    const rows = suratJalanModal.surat_jalan || [];
    if (!rows.length) { Swal.fire('Data Belum Lengkap', 'Tambahkan minimal 1 surat jalan.', 'warning'); return; }
    const hasIncomplete = rows.some(sj => !sj.no_surat_jalan?.trim() || sj.tonase === '' || sj.tonase === null || sj.qty === '' || sj.qty === null);
    if (hasIncomplete) { Swal.fire('Surat Jalan Belum Lengkap', 'Setiap surat jalan wajib memiliki nomor, tonase (Kg), dan qty (Koli).', 'warning'); return; }
    const validSuratJalan = rows.map(sj => ({
      no_surat_jalan: sj.no_surat_jalan.trim(),
      tonase: parseFloat(sj.tonase),
      qty: parseInt(sj.qty, 10),
      direction: sj.direction || 'Masuk',
      keterangan: sj.keterangan?.trim() || null,
    }));
    setIsSavingSuratJalan(true);
    try {
      const response = await fetch(`${SECURITY_API}/${suratJalanModal.log.id}/surat-jalan`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ surat_jalan: validSuratJalan }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Gagal menambahkan surat jalan.');
      const updatedRow = result.data;
      updateLogsWithCache(current => current.map(log => log.id === suratJalanModal.log.id ? {
        ...log, ...updatedRow, type: 'vehicle',
        direction: updatedRow.keluar && !updatedRow.masuk ? 'Keluar' : 'Masuk',
        plate: updatedRow.nopol, officer_name: updatedRow.petugas,
        timestamp: updatedRow.created_at || log.timestamp,
        surat_jalan: updatedRow.surat_jalan || updatedRow.suratJalan || [],
      } : log));
      closeSuratJalanModal();
      Swal.fire({ icon: 'success', title: 'Surat Jalan Berhasil Ditambahkan', timer: 1500, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Gagal Menyimpan', error.message || 'Periksa koneksi lalu coba lagi.', 'error');
    } finally {
      setIsSavingSuratJalan(false);
    }
  };

  const deleteSuratJalan = async (suratJalanId, securityId) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Hapus Surat Jalan?', text: 'Dokumen surat jalan ini akan dihapus.',
      showCancelButton: true, confirmButtonText: 'Ya, Hapus', cancelButtonText: 'Batal', confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      if (suratJalanId && Number(suratJalanId) > 0) {
        const response = await fetch(`${SECURITY_API}/surat-jalan/${suratJalanId}`, { method: 'DELETE', headers: { Accept: 'application/json' } });
        const resData = await response.json();
        if (!response.ok) throw new Error(resData.message || 'Gagal menghapus surat jalan.');
        if (resData.data) {
          const updatedRow = resData.data;
          updateLogsWithCache(current => current.map(log => log.id === securityId ? {
            ...log, ...updatedRow, type: 'vehicle',
            direction: updatedRow.keluar && !updatedRow.masuk ? 'Keluar' : 'Masuk',
            plate: updatedRow.nopol, officer_name: updatedRow.petugas,
            timestamp: updatedRow.created_at || log.timestamp,
            surat_jalan: updatedRow.surat_jalan || updatedRow.suratJalan || [],
          } : log));
        } else {
          updateLogsWithCache(current => current.map(log => log.id === securityId ? {
            ...log, surat_jalan: (log.surat_jalan || log.suratJalan || []).filter(item => item.id !== suratJalanId),
          } : log));
        }
      } else {
        updateLogsWithCache(current => current.map(log => log.id === securityId ? {
          ...log, surat_jalan: (log.surat_jalan || log.suratJalan || []).filter(item => item.id !== suratJalanId),
        } : log));
      }
      Swal.fire({ icon: 'success', title: 'Surat Jalan Dihapus', timer: 1500, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Gagal Menghapus', error.message || 'Periksa koneksi lalu coba lagi.', 'error');
    }
  };

  const handlePatrolPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) { event.target.value = ''; return Swal.fire('Foto Tidak Valid', 'Gunakan JPG/PNG/WEBP maksimal 5 MB.', 'warning'); }
    if (patrolPhotoPreview) URL.revokeObjectURL(patrolPhotoPreview);
    setPatrol(current => ({ ...current, photo: file })); setPatrolPhotoPreview(URL.createObjectURL(file));
  };

  const submitPatrol = async (event) => {
    event.preventDefault();
    const photoRequired = patrol.condition !== 'Aman';
    if (!patrol.location.trim() || (photoRequired && !patrol.photo)) return Swal.fire('Data Belum Lengkap', 'Foto temuan wajib tersedia untuk kondisi tidak aman.', 'warning');
    const formData = new FormData(); const user = getUser();
    Object.entries({ round: patrol.round, location: patrol.location, condition: patrol.condition, findings: patrol.findings, action: patrol.action, latitude: patrol.latitude, longitude: patrol.longitude, accuracy: patrol.accuracy, petugas: user.name }).forEach(([key, value]) => formData.append(key, value));
    if (patrol.photo) formData.append('photo', patrol.photo); setIsSavingPatrol(true);
    try {
      const response = await fetch(`${SECURITY_API}/patrols`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData }); const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Patroli gagal disimpan.');
      updateLogsWithCache(current => [{ ...result.data, type: 'patrol', officer_name: result.data.petugas, timestamp: result.data.created_at }, ...current]);
      setPatrol(current => ({ ...emptyPatrol, round: current.round })); setPatrolPhotoPreview(''); setShowAddForm(false);
      loadActivityFeed();
      Swal.fire({ icon: 'success', title: 'Titik Patroli Tersimpan', text: `Putaran ${patrol.round} · ${patrol.location}`, timer: 1600, showConfirmButton: false });
    } catch (error) { Swal.fire('Gagal Menyimpan', error.message, 'error'); } finally { setIsSavingPatrol(false); }
  };

  const submitActivity = async (event) => {
    event.preventDefault();
    if (!handover.receiver.trim() || !handover.items.length || !giverSignature || !receiverSignature) return Swal.fire('Data Belum Lengkap', 'Petugas penerima, inventaris, dan kedua tanda tangan wajib diisi.', 'warning');
    const user = getUser(); setIsSavingHandover(true);
    try {
      const response = await fetch(`${SECURITY_API}/handovers`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ giver: user.name, receiver: handover.receiver, items: handover.items, condition: handover.condition, notes: handover.notes, giver_signature: giverSignature, receiver_signature: receiverSignature }) });
      const result = await response.json(); if (!response.ok) throw new Error(result.message || 'Serah terima gagal disimpan.');
      setHandover({ receiver: '', condition: 'Lengkap dan baik', notes: '', items: INVENTORY_ITEMS }); setGiverSignature(''); setReceiverSignature(''); setShowAddForm(false); await loadActivityFeed();
      Swal.fire({ icon: 'success', title: 'Serah Terima Tersimpan', timer: 1500, showConfirmButton: false });
    } catch (error) { Swal.fire('Gagal Menyimpan', error.message, 'error'); } finally { setIsSavingHandover(false); }
  };

  const addInventoryItem = () => {
    const item = newInventoryItem.trim().replace(/\s+/g, ' ').slice(0, 50);
    if (!item) return;
    const existingItem = inventoryOptions.find(option => option.toLocaleLowerCase('id-ID') === item.toLocaleLowerCase('id-ID'));
    const selectedItem = existingItem || item;
    if (!existingItem) {
      const customItems = [...readSelectOptions(INVENTORY_OPTIONS_KEY), item];
      localStorage.setItem(INVENTORY_OPTIONS_KEY, JSON.stringify(customItems));
      setInventoryOptions(current => [...current, item]);
    }
    setHandover(current => ({ ...current, items: current.items.some(value => value.toLocaleLowerCase('id-ID') === selectedItem.toLocaleLowerCase('id-ID')) ? current.items : [...current.items, selectedItem] }));
    setNewInventoryItem('');
  };


  const deleteLog = async (id) => {
    const result = await Swal.fire({ icon: 'warning', title: 'Hapus Catatan?', showCancelButton: true, confirmButtonText: 'Hapus', confirmButtonColor: '#ef4444' });
    if (!result.isConfirmed) return;
    const updated = logs.filter(log => log.id !== id);
    setLogs(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const renderForm = () => {
    if (activeTab === 'vehicle') {
      return (
        <form onSubmit={submitVehicle} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { value: 'Masuk', icon: LogIn, active: '#16a34a', soft: '#f0fdf4', border: '#86efac', text: '#166534' },
              { value: 'Keluar', icon: LogOut, active: '#dc2626', soft: '#fef2f2', border: '#fca5a5', text: '#991b1b' },
            ].map(option => {
              const selected = vehicle.direction === option.value; const Icon = option.icon;
              return <button key={option.value} type="button" onClick={() => setVehicle(current => ({
                ...current,
                direction: option.value,
                surat_jalan: (current.surat_jalan || []).map(sj => ({ ...sj, direction: resolveSuratJalanDirection(sj, option.value) })),
              }))} aria-pressed={selected} style={{ padding: 12, borderRadius: 12, border: `2px solid ${selected ? option.active : option.border}`, background: selected ? option.active : option.soft, color: selected ? '#fff' : option.text, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, cursor: 'pointer', boxShadow: selected ? `0 5px 14px ${option.border}` : 'none', transition: 'all .18s ease' }}><Icon size={18} strokeWidth={2.5} />{option.value}</button>;
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Jenis Kendaraan *"><SearchCreateSelect storageKey="security_options_jenis" value={vehicle.jenis} onChange={jenis => setVehicle(current => ({ ...current, jenis }))} placeholder="Cari / tambah jenis" maxLength={10} uppercase /></Field>
            <Field label="Kode Kendaraan"><SearchCreateSelect storageKey="security_options_kode" value={vehicle.kode} onChange={kode => setVehicle(current => ({ ...current, kode }))} placeholder="Cari / tambah kode" maxLength={15} uppercase /></Field>
          </div>
          <Field label="Nomor Polisi *"><SearchCreateSelect storageKey="security_options_nopol" value={vehicle.nopol} onChange={nopol => setVehicle(current => ({ ...current, nopol }))} placeholder="Cari / tambah nomor polisi" maxLength={11} uppercase /></Field>
          <Field label="Nama Pengemudi *"><SearchCreateSelect storageKey="security_options_driver" value={vehicle.driver} onChange={driver => setVehicle(current => ({ ...current, driver }))} placeholder="Cari / tambah pengemudi" maxLength={20} /></Field>
          <Field label="Keterangan" style={{ order: 1 }}><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={vehicle.keterangan} onChange={event => setVehicle(current => ({ ...current, keterangan: event.target.value }))} placeholder="Keperluan kendaraan" /></Field>
          <Field label="Titipan ATK (opsional)" style={{ order: 3 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ padding: 10, borderRadius: 11, background: vehicle.titipan.length ? '#ecfdf5' : '#eff6ff', color: vehicle.titipan.length ? '#047857' : '#475569', fontSize: 10, fontWeight: 800 }}>{vehicle.titipan.length ? `${vehicle.titipan.length} titipan ATK dipilih` : 'Tidak ada titipan dipilih'}</div>
              {isLoadingDeposits ? <div style={{ padding: 12, textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>Memuat titipan yang disiapkan...</div> : preparedDeposits.length ? preparedDeposits.map(item => { const selected = vehicle.titipan.some(id => String(id) === String(item.id)); return <label key={item.id} style={{ padding: 11, borderRadius: 11, border: `1.5px solid ${selected ? '#10b981' : '#e2e8f0'}`, background: selected ? '#ecfdf5' : '#fff', color: '#334155', cursor: 'pointer' }}><div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}><input type="checkbox" value={item.id} checked={selected} onChange={() => setVehicle(current => ({ ...current, titipan: selected ? current.titipan.filter(id => String(id) !== String(item.id)) : [...current.titipan, String(item.id)] }))} /><div style={{ minWidth: 0 }}><div style={{ fontSize: 11, fontWeight: 900, overflowWrap: 'anywhere' }}>ATK {item.agen || 'Agen belum diisi'}</div><div style={{ marginTop: 3, color: '#64748b', fontSize: 9, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{item.catatan ? `Disiapkan ${item.catatan}` : 'Catatan penyiapan belum tersedia'}</div></div></div></label>; }) : <div style={{ padding: 12, border: '1px dashed #cbd5e1', borderRadius: 11, textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>Tidak ada titipan berstatus disiapkan.</div>}
            </div>
          </Field>

          {/* ── Surat Jalan (Relasi Dinamis) ── */}
          <div style={{ order: 2, display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 14, background: '#f8fafc', border: '1.5px dashed #cbd5e1' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 12, fontWeight: 900 }}>
                <FileText size={15} color="#2563eb" />
                <span>Surat Jalan ({vehicle.surat_jalan?.length || 0})</span>
              </div>
              <button
                type="button"
                onClick={addSuratJalan}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '6px 11px', borderRadius: 9,
                  background: '#2563eb', color: '#fff',
                  border: 0, fontSize: 11, fontWeight: 800,
                  cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,.25)'
                }}
              >
                <Plus size={13} strokeWidth={2.5} /> Tambah Surat Jalan
              </button>
            </div>

            {(!vehicle.surat_jalan || vehicle.surat_jalan.length === 0) ? (
              <div style={{ padding: '12px 10px', textAlign: 'center', color: '#94a3b8', fontSize: 11, borderRadius: 10, background: '#fff', border: '1px solid #f1f5f9' }}>
                Tidak ada surat jalan (opsional). Tekan tombol <strong>+ Tambah Surat Jalan</strong> jika armada membawa muatan surat jalan.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {vehicle.surat_jalan.map((sj, index) => (
                  <div key={sj.id || index} style={{ padding: 12, borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 900, color: '#2563eb' }}>
                        📄 Surat Jalan #{index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSuratJalan(index)}
                        title="Hapus Surat Jalan"
                        style={{ border: 0, background: '#fee2e2', color: '#dc2626', width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center', cursor: 'pointer' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                          Arah Surat Jalan *
                          <input
                            type="text"
                            value={resolveSuratJalanDirection(sj, vehicle.direction)}
                            readOnly
                            disabled
                            style={{ ...inputStyle, background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' }}
                          />
                        </label>

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                          No. Surat Jalan *
                          <input
                            type="text"
                            required
                            style={inputStyle}
                            value={sj.no_surat_jalan}
                            onChange={(e) => updateSuratJalan(index, 'no_surat_jalan', e.target.value)}
                            placeholder="Contoh: SJ/2026/08/001"
                          />
                        </label>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                          Tonase (Kg) *
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.01"
                              style={{ ...inputStyle, paddingRight: 38 }}
                              value={sj.tonase}
                              onChange={(e) => updateSuratJalan(index, 'tonase', e.target.value)}
                              placeholder="0.0"
                            />
                            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: '#64748b' }}>
                              Kg
                            </span>
                          </div>
                        </label>

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                          Qty (Koli) *
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              required
                              min="0"
                              step="1"
                              style={{ ...inputStyle, paddingRight: 42 }}
                              value={sj.qty}
                              onChange={(e) => updateSuratJalan(index, 'qty', e.target.value)}
                              placeholder="0"
                            />
                            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: '#64748b' }}>
                              Koli
                            </span>
                          </div>
                        </label>
                      </div>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                        Keterangan (opsional)
                        <input
                          type="text"
                          style={{ ...inputStyle, fontSize: 12, padding: '8px 10px' }}
                          value={sj.keterangan || ''}
                          onChange={(e) => updateSuratJalan(index, 'keterangan', e.target.value)}
                          placeholder="Catatan muatan..."
                        />
                      </label>
                    </div>
                  </div>
                ))}

                {/* Ringkasan Akumulasi Total */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, padding: '10px 8px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div>
                    <div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL SJ</div>
                    <div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>{vehicle.surat_jalan.length} Dok</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL TONASE</div>
                    <div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>
                      {vehicle.surat_jalan.reduce((acc, curr) => acc + (parseFloat(curr.tonase) || 0), 0).toLocaleString('id-ID')} Kg
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL QTY</div>
                    <div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>
                      {vehicle.surat_jalan.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0).toLocaleString('id-ID')} Koli
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Field label="Memo" style={{ order: 4 }}><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={vehicle.memo} onChange={event => setVehicle(current => ({ ...current, memo: event.target.value }))} placeholder="Catatan tambahan" /></Field>
          <Field label="Foto Kendaraan *" style={{ order: 5 }}>
            <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handlePhoto} style={{ display: 'none' }} />
            <button type="button" onClick={() => photoInputRef.current?.click()} style={{ minHeight: 132, border: '2px dashed #93c5fd', borderRadius: 16, background: '#eff6ff', color: '#2563eb', overflow: 'hidden', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
              {photoPreview ? <img src={photoPreview} alt="Pratinjau kendaraan" style={{ width: '100%', height: 180, objectFit: 'cover' }} /> : <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}><Camera size={28} /><span>Ambil / pilih foto</span><small style={{ color: '#64748b' }}>JPG, PNG, WEBP · maks. 5 MB</small></span>}
            </button>
          </Field>
          <div style={{ order: 6 }}><SubmitButton disabled={isSavingVehicle} icon={isSavingVehicle ? Loader2 : UploadCloud} label={isSavingVehicle ? 'Menyimpan...' : `Simpan Kendaraan ${vehicle.direction}`} /></div>
        </form>
      );
    }

    if (activeTab === 'patrol') {
      return (
        <form onSubmit={submitPatrol} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: 12, borderRadius: 13, background: '#f0fdfa', border: '1px solid #99f6e4' }}><div style={{ fontSize: 10, color: '#0f766e', fontWeight: 800 }}>PUTARAN {patrol.round}</div><div style={{ marginTop: 3, color: '#134e4a', fontSize: 16, fontWeight: 900 }}>{patrol.location}</div></div>
          <Field label="Kondisi">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 7 }}>
              {[
                { value: 'Aman', activeBg: '#16a34a', softBg: '#f0fdf4', border: '#86efac', text: '#166534' },
                { value: 'Perlu Perhatian', activeBg: '#eab308', softBg: '#fefce8', border: '#fde047', text: '#854d0e' },
                { value: 'Darurat', activeBg: '#dc2626', softBg: '#fef2f2', border: '#fca5a5', text: '#991b1b' },
              ].map(option => {
                const selected = patrol.condition === option.value;
                return <button key={option.value} type="button" onClick={() => setPatrol(current => ({ ...current, condition: option.value }))} aria-pressed={selected} style={{ minWidth: 0, minHeight: 54, padding: '8px 5px', border: `2px solid ${selected ? option.activeBg : option.border}`, borderRadius: 12, background: selected ? option.activeBg : option.softBg, color: selected ? '#fff' : option.text, fontSize: 10, lineHeight: 1.25, fontWeight: 900, cursor: 'pointer', boxShadow: selected ? `0 4px 12px ${option.border}` : 'none', transition: 'all .18s ease' }}>{selected ? '✓ ' : ''}{option.value}</button>;
              })}
            </div>
          </Field>
          <Field label="Temuan"><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={patrol.findings} onChange={event => setPatrol(current => ({ ...current, findings: event.target.value }))} placeholder="Temuan saat patroli" /></Field>
          <Field label="Tindakan"><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={patrol.action} onChange={event => setPatrol(current => ({ ...current, action: event.target.value }))} placeholder="Tindakan yang dilakukan" /></Field>
          {patrol.condition !== 'Aman' && <Field label="Foto Temuan *"><input ref={patrolPhotoRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handlePatrolPhoto} style={{ display: 'none' }} /><button type="button" onClick={() => patrolPhotoRef.current?.click()} style={{ minHeight: 120, border: '2px dashed #fca5a5', borderRadius: 15, background: '#fef2f2', color: '#b91c1c', overflow: 'hidden' }}>{patrolPhotoPreview ? <img src={patrolPhotoPreview} alt="Temuan patroli" style={{ width: '100%', height: 170, objectFit: 'cover' }} /> : <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}><Camera size={26} />Lampirkan foto kondisi tidak aman</span>}</button></Field>}
          <SubmitButton disabled={isSavingPatrol} icon={isSavingPatrol ? Loader2 : MapPinned} label={isSavingPatrol ? 'Menyimpan...' : `Simpan Titik · Putaran ${patrol.round}`} />
        </form>
      );
    }

    if (activeTab === 'activity') return (
      <form onSubmit={submitActivity} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ padding: 12, borderRadius: 13, background: '#eff6ff', color: '#1e40af', fontSize: 11 }}>Petugas menyerahkan: <strong>{getUser().name}</strong></div>
        <Field label="Petugas Security Penerima *"><SearchCreateSelect storageKey="security_options_officers" value={handover.receiver} onChange={receiver => setHandover(current => ({ ...current, receiver }))} placeholder="Cari / tambah nama petugas" maxLength={50} /></Field>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>Inventaris yang diserahkan *</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{inventoryOptions.map(item => { const checked = handover.items.includes(item); return <label key={item} style={{ padding: 9, borderRadius: 10, background: checked ? '#ecfdf5' : '#f8fafc', border: `1px solid ${checked ? '#6ee7b7' : '#e2e8f0'}`, color: '#334155', fontSize: 10, fontWeight: 700 }}><input type="checkbox" checked={checked} onChange={() => setHandover(current => ({ ...current, items: checked ? current.items.filter(value => value !== item) : [...current.items, item] }))} /> {item}</label>; })}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8 }}>
          <input type="text" maxLength={50} value={newInventoryItem} onChange={event => setNewInventoryItem(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addInventoryItem(); } }} placeholder="Tambah inventaris manual" style={inputStyle} />
          <button type="button" onClick={addInventoryItem} disabled={!newInventoryItem.trim()} style={{ border: 0, borderRadius: 11, padding: '0 14px', background: newInventoryItem.trim() ? '#0d9488' : '#cbd5e1', color: '#fff', fontSize: 11, fontWeight: 800, cursor: newInventoryItem.trim() ? 'pointer' : 'default' }}>Tambah</button>
        </div>
        <Field label="Kondisi Inventaris"><select style={inputStyle} value={handover.condition} onChange={event => setHandover(current => ({ ...current, condition: event.target.value }))}><option>Lengkap dan baik</option><option>Lengkap, ada kerusakan</option><option>Tidak lengkap</option></select></Field>
        <Field label="Catatan"><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={handover.notes} onChange={event => setHandover(current => ({ ...current, notes: event.target.value }))} placeholder="Kerusakan, kekurangan, atau pesan untuk shift berikutnya" /></Field>
        <SignaturePad label="Tanda Tangan Yang Menyerahkan *" signer={getUser().name} value={giverSignature} onChange={setGiverSignature} />
        <SignaturePad label="Tanda Tangan Yang Menerima *" signer={handover.receiver || 'Pilih petugas penerima terlebih dahulu'} value={receiverSignature} onChange={setReceiverSignature} />
        <SubmitButton disabled={isSavingHandover} icon={isSavingHandover ? Loader2 : ClipboardList} label={isSavingHandover ? 'Menyimpan...' : 'Simpan Serah Terima'} />
      </form>
    );

    return null;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)', display: 'flex', flexDirection: 'column', fontFamily: 'Poppins, sans-serif' }}>
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: '#F5F9FF', padding: 15, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ width: 40, height: 40, flexShrink: 0, border: 'none', borderRadius: '50%', background: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 2px 6px rgba(0,0,0,.15)', cursor: 'pointer' }}><ArrowLeft size={22} color="#333" /></button>
        <div style={{ flex: 1, textAlign: 'center', marginRight: 40 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#003087' }}>Security</h2>
        </div>
      </header>

      <main style={{ flex: 1, overflowY: 'auto', padding: '15px 16px 90px', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: 620, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TABS.length}, 1fr)`, background: '#fff', borderRadius: 24, padding: 6, border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,.08)', margin: '0 auto 20px', boxSizing: 'border-box' }}>
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return <button key={tab.key} onClick={() => { setActiveTab(tab.key); setHistorySearch(''); }} style={{ border: 'none', borderRadius: 18, background: active ? '#eff6ff' : 'transparent', color: active ? '#2563eb' : '#64748b', padding: '11px 3px', fontSize: 10, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'pointer', transition: 'all .2s' }}><Icon size={18} />{tab.label}</button>;
            })}
          </div>

          {activeTab !== 'patrol' && <button type="button" onClick={() => setShowAddForm(true)} style={{ width: '100%', border: 0, borderRadius: 18, padding: 15, marginBottom: 24, background: '#2563eb', color: '#fff', boxShadow: '0 6px 18px rgba(37,99,235,.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}><Plus size={18} />{activeTab === 'activity' ? 'Serah Terima Inventaris' : `Tambah Catatan ${TABS.find(tab => tab.key === activeTab)?.label}`}</button>}

          <section style={{ background: '#fff', borderRadius: 24, padding: '24px 20px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,.08)', boxSizing: 'border-box' }}>
            <div style={{ textAlign: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
              <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px', color: '#111827' }}>{activeTab === 'vehicle' ? 'Riwayat Keluar Masuk Kendaraan (2 Hari)' : activeTab === 'patrol' ? 'Timeline Patroli (2 Hari)' : 'Riwayat Hari Ini'}</h2>
              <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{activeTab === 'patrol' ? 'Periksa seluruh titik pada setiap putaran' : activeTab === 'activity' ? `${visibleActivityFeed.length} aktivitas ${currentUser.name}` : `${visibleLogs.length} ${activeTab === 'vehicle' ? `kendaraan (${vehicleStats.incomplete} belum lengkap · ${vehicleStats.complete} lengkap)` : 'catatan tersimpan'}`}</p>
            </div>
            {activeTab === 'patrol' && <details style={{ margin: '-8px 0 18px', border: '1px solid #bfdbfe', borderRadius: 14, background: '#f8fbff', overflow: 'hidden' }}>
              <summary style={{ padding: '12px 14px', color: '#1e40af', fontSize: 12, fontWeight: 900, cursor: 'pointer', listStylePosition: 'inside' }}>Panduan pemeriksaan setiap titik</summary>
              <div style={{ padding: '0 14px 14px', display: 'grid', gap: 10 }}>
                {[
                  ['Listrik & perangkat', 'Pastikan lampu, kipas angin, AC, komputer, televisi, charger, dispenser, mesin, dan perangkat elektronik lain tidak menyala saat sedang tidak digunakan.'],
                  ['Kondisi lingkungan', 'Periksa kebersihan, lantai licin, genangan air, bau tidak wajar, kebocoran air/atap, saluran tersumbat, serta pencahayaan dan sirkulasi udara.'],
                  ['Kerapian & jalur akses', 'Pastikan tidak ada barang berserakan, kabel melintang, tumpukan tidak stabil, pintu darurat terhalang, atau barang yang mengganggu jalan dan akses kendaraan.'],
                  ['Keamanan area', 'Periksa pintu, jendela, pagar, gembok, kendaraan, barang berharga, orang tidak dikenal, dan aktivitas mencurigakan di sekitar titik.'],
                  ['Risiko kebakaran', 'Perhatikan bau terbakar, kabel terkelupas, stop kontak panas/bertumpuk, bahan mudah terbakar, puntung rokok, serta akses APAR dan hydrant.'],
                  ['Perhatian khusus', 'Laporkan kerusakan fasilitas, suara mesin tidak normal, hewan berbahaya, pohon/benda rawan jatuh, toilet bermasalah, dan kondisi lain yang berpotensi membahayakan orang atau aset.'],
                ].map(([title, text]) => <div key={title} style={{ display: 'grid', gridTemplateColumns: '8px minmax(0, 1fr)', gap: 9 }}><span style={{ width: 8, height: 8, marginTop: 5, borderRadius: '50%', background: '#3b82f6' }} /><div><div style={{ color: '#334155', fontSize: 11, fontWeight: 900 }}>{title}</div><div style={{ marginTop: 2, color: '#64748b', fontSize: 10, lineHeight: 1.5 }}>{text}</div></div></div>)}
                <div style={{ padding: 10, borderRadius: 10, background: '#fff7ed', color: '#9a3412', fontSize: 10, lineHeight: 1.5 }}><strong>Jika ditemukan kondisi tidak aman:</strong> pilih Perlu Perhatian atau Darurat, jelaskan temuannya, tuliskan tindakan yang dilakukan, dan lampirkan foto sebagai bukti.</div>
              </div>
            </details>}
            {activeTab === 'patrol' && <div style={{ marginBottom: 14, border: '1px solid #dbe3ee', borderRadius: 14, background: '#f8fafc', overflow: 'hidden' }}>
              <button type="button" onClick={() => setShowPatrolFilters(value => !value)} aria-expanded={showPatrolFilters} style={{ width: '100%', padding: '11px 12px', border: 0, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <span style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 10, background: showPatrolFilters ? '#dbeafe' : '#e2e8f0', color: showPatrolFilters ? '#2563eb' : '#64748b', display: 'grid', placeItems: 'center' }}><SlidersHorizontal size={16} /></span>
                <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: 'block', color: '#94a3b8', fontSize: 9, fontWeight: 900, letterSpacing: '.05em' }}>TAMPILAN PATROLI</span><span style={{ display: 'block', marginTop: 3, color: '#334155', fontSize: 11, fontWeight: 900 }}>{patrolViewDate === activePatrolShift.date ? 'Hari Ini' : 'Kemarin'} · Shift {patrolViewShift}<span style={{ color: '#94a3b8', fontWeight: 700 }}> · {patrolViewDate}</span></span></span>
                <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 8px', borderRadius: 9, background: showPatrolFilters ? '#dbeafe' : '#e2e8f0', color: showPatrolFilters ? '#1d4ed8' : '#64748b', fontSize: 9, fontWeight: 900 }}>{showPatrolFilters ? 'Sembunyikan' : 'Tampilkan'}{showPatrolFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
              </button>
              {showPatrolFilters && <div style={{ padding: '0 12px 12px', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ margin: '10px 0 6px', color: '#64748b', fontSize: 9, fontWeight: 900 }}>PILIH HARI</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 7 }}>{[{ value: activePatrolShift.date, label: 'Hari Ini' }, { value: (() => { const date = new Date(`${activePatrolShift.date}T12:00:00`); date.setDate(date.getDate() - 1); return toLocalDateKey(date); })(), label: 'Kemarin' }].map(option => { const active = patrolViewDate === option.value; return <button type="button" onClick={() => setPatrolViewDate(option.value)} key={option.value} style={{ padding: '9px 5px', borderRadius: 10, background: active ? '#dbeafe' : '#fff', color: active ? '#1d4ed8' : '#64748b', border: `1.5px solid ${active ? '#3b82f6' : '#dbe3ee'}`, fontSize: 10, fontWeight: 900 }}>{option.label}<small style={{ display: 'block', marginTop: 2 }}>{option.value}</small></button>; })}</div>
                <div style={{ margin: '10px 0 6px', color: '#64748b', fontSize: 9, fontWeight: 900 }}>PILIH SHIFT</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 7 }}>{[{ value: '1', label: 'Shift 1', time: '07:00–19:00' }, { value: '2', label: 'Shift 2', time: '19:00–07:00' }].map(option => { const active = patrolViewShift === option.value; return <button type="button" onClick={() => setPatrolViewShift(option.value)} key={option.value} style={{ padding: '9px 5px', borderRadius: 10, background: active ? '#fef3c7' : '#fff', color: active ? '#92400e' : '#64748b', border: `1.5px solid ${active ? '#f59e0b' : '#dbe3ee'}`, fontSize: 10, fontWeight: 900 }}>{option.label}<small style={{ display: 'block', marginTop: 2 }}>{option.time}</small></button>; })}</div>
                <div style={{ marginTop: 9, color: '#94a3b8', fontSize: 9, lineHeight: 1.4 }}>Buka bagian ini hanya saat ingin melihat riwayat hari atau shift lain.</div>
              </div>}
            </div>}
            {activeTab === 'patrol' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>{['1', '2', '3'].map(round => { const count = logs.filter(log => log.type === 'patrol' && log.patrol_date === patrolViewDate && String(log.shift_number || '1') === patrolViewShift && String(log.round) === round).length; const active = patrolViewRound === round; return <button type="button" onClick={() => setPatrolViewRound(round)} key={round} style={{ padding: '10px 5px', borderRadius: 11, textAlign: 'center', background: active ? '#ccfbf1' : '#f8fafc', border: `2px solid ${active ? '#0d9488' : '#e2e8f0'}` }}><div style={{ fontSize: 10, fontWeight: 900, color: active ? '#0f766e' : '#64748b' }}>Putaran {round}</div><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{count}/{PATROL_POINTS.length} titik</div></button>; })}</div>}
            {activeTab !== 'patrol' && <div style={{ position: 'relative', marginBottom: activeTab === 'vehicle' ? 12 : 18 }}>
              <Search size={17} color="#94a3b8" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input type="search" value={historySearch} onChange={event => setHistorySearch(event.target.value)} placeholder={activeTab === 'activity' ? 'Cari jam atau perincian kegiatan...' : 'Cari nopol, driver, jenis, kode, keterangan...'} style={{ ...inputStyle, paddingLeft: 40, paddingRight: historySearch ? 40 : 12, borderRadius: 14, background: '#f8fafc' }} />
              {historySearch && <button type="button" onClick={() => setHistorySearch('')} aria-label="Hapus pencarian" style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, border: 0, borderRadius: 9, background: '#e2e8f0', color: '#64748b', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={15} /></button>}
            </div>}

            {/* Filter Status Kendaraan (Semua / Belum Lengkap / Sudah Lengkap) */}
            {activeTab === 'vehicle' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 16 }}>
                {[
                  { key: 'all', label: 'Semua', count: vehicleStats.total, activeBg: '#eff6ff', activeBorder: '#3b82f6', activeText: '#1d4ed8' },
                  { key: 'incomplete', label: 'Belum Lengkap', count: vehicleStats.incomplete, activeBg: '#fff7ed', activeBorder: '#f97316', activeText: '#c2410c' },
                  { key: 'complete', label: 'Sudah Lengkap', count: vehicleStats.complete, activeBg: '#ecfdf5', activeBorder: '#10b981', activeText: '#047857' },
                ].map(f => {
                  const active = vehicleFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setVehicleFilter(f.key)}
                      style={{
                        padding: '8px 4px',
                        borderRadius: 12,
                        border: `1.5px solid ${active ? f.activeBorder : '#e2e8f0'}`,
                        background: active ? f.activeBg : '#f8fafc',
                        color: active ? f.activeText : '#64748b',
                        fontSize: 11,
                        fontWeight: 800,
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all .15s ease',
                        boxShadow: active ? '0 2px 6px rgba(0,0,0,.04)' : 'none'
                      }}
                    >
                      <div>{f.label}</div>
                      <div style={{ fontSize: 10, marginTop: 2, opacity: 0.85 }}>({f.count})</div>
                    </button>
                  );
                })}
              </div>
            )}

            {activeTab === 'patrol' ? <PatrolTimeline points={PATROL_POINTS} round={patrolViewRound} date={patrolViewDate} shift={patrolViewShift} logs={logs} onCheck={patrolViewDate === activePatrolShift.date && patrolViewShift === activePatrolShift.number ? openPatrolPoint : null} /> : activeTab === 'activity' ? <ActivityTimeline items={visibleActivityFeed} keyword={historySearch} user={currentUser} /> : activeTab === 'vehicle' && isLoadingVehicle ? <div style={{ padding: 28, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Memuat data dari database...</div> : visibleLogs.length ? visibleLogs.map(log => <LogCard key={log.id} log={log} onDelete={deleteLog} onComplete={openCompletion} onAddSuratJalan={openSuratJalanModal} onPreviewImage={setPreviewImage} onDeleteSuratJalan={deleteSuratJalan} />) : <div style={{ padding: 28, textAlign: 'center', background: '#fff', borderRadius: 18, border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: 12 }}>{historySearch ? `Tidak ada data yang cocok dengan “${historySearch}”.` : 'Belum ada pencatatan pada tab ini.'}</div>}
          </section>
        </div>
      </main>
      {showAddForm && (
        <div onClick={() => setShowAddForm(false)} style={{ position: 'fixed', inset: 0, zIndex: 450, background: 'rgba(15,23,42,.48)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={event => event.stopPropagation()} style={{ width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: '26px 26px 0 0', padding: '12px 20px 28px', boxSizing: 'border-box', boxShadow: '0 -12px 40px rgba(15,23,42,.2)' }}>
            <div style={{ width: 42, height: 5, borderRadius: 5, background: '#cbd5e1', margin: '0 auto 14px' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 14, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }}>
              <div><h3 style={{ margin: 0, fontSize: 17, color: '#0f172a' }}>{activeTab === 'activity' ? 'Serah Terima Inventaris' : 'Tambah Catatan'}</h3><p style={{ margin: '3px 0 0', color: '#64748b', fontSize: 12 }}>{TABS.find(tab => tab.key === activeTab)?.label}</p></div>
              <button type="button" onClick={() => setShowAddForm(false)} style={{ border: 0, background: '#f1f5f9', width: 36, height: 36, borderRadius: 12, display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            {renderForm()}
          </div>
        </div>
      )}
      {completion && (
        <div onClick={closeCompletion} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,.48)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <form onSubmit={submitCompletion} onClick={event => event.stopPropagation()} style={{ width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '26px 26px 0 0', padding: '12px 20px 24px', boxSizing: 'border-box', boxShadow: '0 -12px 40px rgba(15,23,42,.2)' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', paddingBottom: 10 }}>
              <div style={{ width: 42, height: 5, borderRadius: 5, background: '#cbd5e1', margin: '0 auto 14px' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div><h3 style={{ margin: 0, fontSize: 17, color: '#0f172a' }}>Lengkapi Jam {completion.action === 'masuk' ? 'Masuk' : 'Keluar'}</h3><p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>{completion.log.nopol} · {completion.log.driver}</p></div>
                <button type="button" onClick={closeCompletion} style={{ border: 0, background: '#f1f5f9', width: 36, height: 36, borderRadius: 12, display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={18} /></button>
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 14, background: '#eff6ff', color: '#1e40af', fontSize: 12, marginBottom: 14 }}>Jam otomatis diisi waktu sekarang dan dapat disesuaikan sebelum disimpan.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label={`Jam ${completion.action === 'masuk' ? 'Masuk' : 'Keluar'} *`}><input type="time" required style={inputStyle} value={completion.time} onChange={event => setCompletion(current => ({ ...current, time: event.target.value }))} /></Field>
              <Field label="Keterangan"><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={completion.keterangan} onChange={event => setCompletion(current => ({ ...current, keterangan: event.target.value }))} placeholder="Tambahkan atau perbarui keterangan" /></Field>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 14, background: '#f8fafc', border: '1.5px dashed #cbd5e1' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 12, fontWeight: 900 }}>
                    <FileText size={15} color="#2563eb" />
                    <span>Surat Jalan ({completion.surat_jalan?.length || 0})</span>
                  </div>
                  <button type="button" onClick={addCompletionSuratJalan} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 9, background: '#2563eb', color: '#fff', border: 0, fontSize: 11, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,.25)' }}>
                    <Plus size={13} strokeWidth={2.5} /> Tambah Surat Jalan
                  </button>
                </div>

                {(!completion.surat_jalan || completion.surat_jalan.length === 0) ? (
                  <div style={{ padding: '12px 10px', textAlign: 'center', color: '#94a3b8', fontSize: 11, borderRadius: 10, background: '#fff', border: '1px solid #f1f5f9' }}>
                    Tidak ada surat jalan. Tekan tombol <strong>+ Tambah Surat Jalan</strong> jika armada membawa muatan surat jalan.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(completion.surat_jalan || []).map((sj, index) => (
                      <div key={sj.id || index} style={{ padding: 12, borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,.03)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 900, color: '#2563eb' }}>📄 Surat Jalan #{index + 1}</span>
                          <button type="button" onClick={() => removeCompletionSuratJalan(index)} title="Hapus Surat Jalan" style={{ border: 0, background: '#fee2e2', color: '#dc2626', width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                            Arah Surat Jalan *
                            <input type="text" value={resolveCompletionSuratJalanDirection(completion.action)} readOnly disabled style={{ ...inputStyle, background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' }} />
                          </label>

                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                            No. Surat Jalan *
                            <input type="text" style={inputStyle} value={sj.no_surat_jalan} onChange={event => updateCompletionSuratJalan(index, 'no_surat_jalan', event.target.value)} placeholder="Contoh: SJ/2026/08/001" />
                          </label>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                            Tonase (Kg) *
                            <div style={{ position: 'relative' }}>
                              <input type="number" min="0" step="0.01" style={{ ...inputStyle, paddingRight: 38 }} value={sj.tonase} onChange={event => updateCompletionSuratJalan(index, 'tonase', event.target.value)} placeholder="0.0" />
                              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: '#64748b' }}>Kg</span>
                            </div>
                          </label>

                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                            Qty (Koli) *
                            <div style={{ position: 'relative' }}>
                              <input type="number" min="0" step="1" style={{ ...inputStyle, paddingRight: 42 }} value={sj.qty} onChange={event => updateCompletionSuratJalan(index, 'qty', event.target.value)} placeholder="0" />
                              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: '#64748b' }}>Koli</span>
                            </div>
                          </label>
                        </div>

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569', marginTop: 8 }}>
                          Keterangan (opsional)
                          <input type="text" style={{ ...inputStyle, fontSize: 12, padding: '8px 10px' }} value={sj.keterangan || ''} onChange={event => updateCompletionSuratJalan(index, 'keterangan', event.target.value)} placeholder="Catatan muatan..." />
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 16 }}><SubmitButton disabled={isCompleting} icon={isCompleting ? Loader2 : Plus} label={isCompleting ? 'Menyimpan...' : `Simpan Jam ${completion.action === 'masuk' ? 'Masuk' : 'Keluar'}`} /></div>
          </form>
        </div>
      )}
      {suratJalanModal && (
        <div onClick={closeSuratJalanModal} style={{ position: 'fixed', inset: 0, zIndex: 510, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <form onSubmit={submitSuratJalanModal} onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '26px 26px 0 0', padding: '12px 20px 28px', boxSizing: 'border-box', boxShadow: '0 -12px 40px rgba(15,23,42,.2)' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', paddingBottom: 10 }}>
              <div style={{ width: 42, height: 5, borderRadius: 5, background: '#cbd5e1', margin: '0 auto 14px' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div><h3 style={{ margin: 0, fontSize: 17, color: '#0f172a' }}>Lengkapi Surat Jalan</h3><p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>{suratJalanModal.log.nopol} · {suratJalanModal.log.driver}</p></div>
                <button type="button" onClick={closeSuratJalanModal} style={{ border: 0, background: '#f1f5f9', width: 36, height: 36, borderRadius: 12, display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={18} /></button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: '#334155' }}>Daftar Surat Jalan</div>
                <button type="button" onClick={addModalSuratJalanRow} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 9, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                  <Plus size={13} strokeWidth={2.5} /> Tambah Baris SJ
                </button>
              </div>

              {(suratJalanModal.surat_jalan || []).map((sj, index) => (
                <div key={sj.id || index} style={{ padding: 12, borderRadius: 14, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 900, color: '#2563eb' }}>📄 Surat Jalan #{index + 1}</span>
                    {(suratJalanModal.surat_jalan?.length > 1) && (
                      <button type="button" onClick={() => removeModalSuratJalanRow(index)} title="Hapus Surat Jalan" style={{ border: 0, background: '#fee2e2', color: '#dc2626', width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                      Arah Surat Jalan *
                      <select style={inputStyle} value={sj.direction || 'Masuk'} onChange={(e) => updateModalSuratJalanRow(index, 'direction', e.target.value)}>
                        <option value="Masuk">📥 Masuk</option>
                        <option value="Keluar">📤 Keluar</option>
                      </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                      No. Surat Jalan *
                      <input type="text" required style={inputStyle} value={sj.no_surat_jalan} onChange={(e) => updateModalSuratJalanRow(index, 'no_surat_jalan', e.target.value)} placeholder="Contoh: SJ/2026/08/001" />
                    </label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                      Tonase (Kg) *
                      <div style={{ position: 'relative' }}>
                        <input type="number" required min="0" step="0.01" style={{ ...inputStyle, paddingRight: 38 }} value={sj.tonase} onChange={(e) => updateModalSuratJalanRow(index, 'tonase', e.target.value)} placeholder="0.0" />
                        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: '#64748b' }}>Kg</span>
                      </div>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                      Qty (Koli) *
                      <div style={{ position: 'relative' }}>
                        <input type="number" required min="0" step="1" style={{ ...inputStyle, paddingRight: 42 }} value={sj.qty} onChange={(e) => updateModalSuratJalanRow(index, 'qty', e.target.value)} placeholder="0" />
                        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: '#64748b' }}>Koli</span>
                      </div>
                    </label>
                  </div>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#475569' }}>
                    Keterangan (opsional)
                    <input type="text" style={{ ...inputStyle, fontSize: 12, padding: '8px 10px' }} value={sj.keterangan || ''} onChange={(e) => updateModalSuratJalanRow(index, 'keterangan', e.target.value)} placeholder="Catatan muatan..." />
                  </label>
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, padding: '10px 8px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                <div><div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL SJ</div><div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>{suratJalanModal.surat_jalan.length} Dok</div></div>
                <div><div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL TONASE</div><div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>{suratJalanModal.surat_jalan.reduce((acc, curr) => acc + (parseFloat(curr.tonase) || 0), 0).toLocaleString('id-ID')} Kg</div></div>
                <div><div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL QTY</div><div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>{suratJalanModal.surat_jalan.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0).toLocaleString('id-ID')} Koli</div></div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}><SubmitButton disabled={isSavingSuratJalan} icon={isSavingSuratJalan ? Loader2 : Plus} label={isSavingSuratJalan ? 'Menyimpan...' : 'Simpan Surat Jalan'} /></div>
          </form>
        </div>
      )}
      {previewImage && (
        <div onClick={() => setPreviewImage(null)} style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(15, 23, 42, 0.88)', backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '92vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 10, color: '#fff' }}>
              <div style={{ fontSize: 14, fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 44px)' }}>{previewImage.title || 'Foto Kendaraan'}</div>
              <button type="button" onClick={() => setPreviewImage(null)} style={{ border: 0, background: 'rgba(255,255,255,0.2)', color: '#fff', width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', cursor: 'pointer', backdropFilter: 'blur(4px)' }}><X size={20} /></button>
            </div>
            <img src={previewImage.url} alt="Preview Full" style={{ maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 18, boxShadow: '0 20px 50px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.15)', background: '#000' }} />
          </div>
        </div>
      )}
    </div>
  );
}

const activityNarrative = (item) => {
  if (item.type !== 'vehicle') return [item.title, item.description].filter(Boolean).join('. ');
  const plateFromTitle = String(item.title || '').split('·').pop().trim();
  const vehicleName = [item.jenis, item.kode, item.nopol || plateFromTitle].filter(Boolean).join(' ');
  const movement = item.direction || (String(item.title).toLowerCase().includes('keluar') ? 'keluar' : 'masuk');
  const driverInfo = item.driver ? ` yang dikemudikan ${item.driver}` : '';
  const note = item.keterangan ? ` Keterangan: ${item.keterangan}.` : '';
  return `Membantu memarkirkan armada ${vehicleName || 'kendaraan'} saat ${movement}${driverInfo}.${note}`;
};

const ActivityTimeline = ({ items, keyword, user }) => {
  const latestHandover = [...items].reverse().find(item => item.type === 'handover');
  const inventory = Array.isArray(latestHandover?.items) && latestHandover.items.length ? latestHandover.items : INVENTORY_ITEMS;
  const inventoryNote = [latestHandover?.condition, latestHandover?.notes].filter(Boolean).join(' · ') || 'Lengkap dan baik';
  const normalizedKeyword = keyword.trim().toLocaleLowerCase('id-ID');
  const filtered = items.filter(item => {
    const searchableText = [
      item.time, item.type, item.title, item.description, activityNarrative(item),
      item.petugas, item.giver, item.receiver, item.jenis, item.kode, item.nopol,
      item.driver, item.keterangan, item.location, item.condition, item.findings,
      item.action, item.notes, ...(Array.isArray(item.items) ? item.items : []),
    ].filter(Boolean).join(' ').toLocaleLowerCase('id-ID');
    return searchableText.includes(normalizedKeyword);
  });
  const today = new Date();
  const day = today.toLocaleDateString('id-ID', { weekday: 'long' });
  const date = today.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const cellStyle = { padding: '10px 8px', border: '1px solid #cbd5e1', verticalAlign: 'top', fontSize: 10, lineHeight: 1.55, color: '#334155', overflowWrap: 'anywhere', wordBreak: 'break-word' };
  const headingStyle = { ...cellStyle, background: '#eff6ff', color: '#1e3a8a', fontWeight: 900, textAlign: 'left' };
  const infoCardStyle = { minWidth: 0, padding: 12, border: '1px solid #dbe3ee', borderRadius: 14, background: '#f8fafc' };
  const infoLabelStyle = { marginBottom: 5, color: '#64748b', fontSize: 9, fontWeight: 900, letterSpacing: '.04em', textTransform: 'uppercase' };
  const infoValueStyle = { color: '#1e293b', fontSize: 11, lineHeight: 1.55, fontWeight: 700, overflowWrap: 'anywhere', wordBreak: 'break-word' };

  return <div style={{ width: '100%', minWidth: 0 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 135px), 1fr))', gap: 9, marginBottom: 18 }}>
      <div style={infoCardStyle}><div style={infoLabelStyle}>Hari</div><div style={infoValueStyle}>{day}<br />{date}</div></div>
      <div style={infoCardStyle}><div style={infoLabelStyle}>Petugas Security</div><div style={{ ...infoValueStyle, fontSize: 12 }}>{user.name}</div><div style={{ marginTop: 3, color: '#64748b', fontSize: 10, lineHeight: 1.45 }}>{user.position}</div></div>
      <div style={{ ...infoCardStyle, gridColumn: '1 / -1' }}><div style={infoLabelStyle}>Inventaris</div><div style={infoValueStyle}>{inventory.join(', ')}</div></div>
      <div style={{ ...infoCardStyle, gridColumn: '1 / -1', background: '#eff6ff', borderColor: '#bfdbfe' }}><div style={{ ...infoLabelStyle, color: '#2563eb' }}>Keterangan</div><div style={infoValueStyle}>{inventoryNote}</div></div>
    </div>
    <div style={{ width: '100%', minWidth: 0, overflow: 'hidden', border: '1px solid #cbd5e1', borderRadius: 14 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup><col style={{ width: 58 }} /><col /></colgroup>
        <thead><tr><th style={{ ...headingStyle, textAlign: 'center' }}>Jam</th><th style={{ ...headingStyle, textAlign: 'center' }}>Perincian Kegiatan</th></tr></thead>
        <tbody>{filtered.length ? filtered.map((item, index) => <tr key={`${item.type}-${item.id}-${index}`}><td style={{ ...cellStyle, textAlign: 'center', fontWeight: 800 }}>{String(item.time || '').slice(0, 5)}</td><td style={cellStyle}>{activityNarrative(item)}</td></tr>) : <tr><td colSpan={2} style={{ ...cellStyle, padding: 24, textAlign: 'center', color: '#94a3b8' }}>{keyword ? 'Aktivitas tidak ditemukan.' : 'Belum ada aktivitas hari ini.'}</td></tr>}</tbody>
      </table>
    </div>
    {latestHandover && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', marginTop: 16, overflow: 'hidden', border: '1px solid #cbd5e1', borderRadius: 14 }}>
      {[
        { label: 'Yang Menyerahkan', name: latestHandover.giver || user.name, signature: latestHandover.giver_signature },
        { label: 'Yang Menerima', name: latestHandover.receiver, signature: latestHandover.receiver_signature },
      ].map((party, index) => <div key={party.label} style={{ minWidth: 0, padding: 12, textAlign: 'center', borderLeft: index ? '1px solid #cbd5e1' : 0 }}><div style={{ color: '#475569', fontSize: 10, fontWeight: 900 }}>{party.label}</div><div style={{ height: 72, display: 'grid', placeItems: 'center' }}>{party.signature ? <img src={party.signature} alt={`Tanda tangan ${party.name}`} style={{ maxWidth: '100%', maxHeight: 68, objectFit: 'contain' }} /> : <span style={{ color: '#cbd5e1', fontSize: 9 }}>Belum ada tanda tangan</span>}</div><div style={{ color: '#1e293b', fontSize: 10, lineHeight: 1.4, fontWeight: 800, overflowWrap: 'anywhere' }}>{party.name}</div></div>)}
    </div>}
  </div>;
};

const PatrolTimeline = ({ points, round, date, shift, logs, onCheck }) => (
  <div style={{ padding: '2px 0' }}>
    {points.map((point, index) => {
      const record = logs.find(log => log.type === 'patrol' && log.patrol_date === date && String(log.shift_number || '1') === String(shift) && String(log.round) === String(round) && log.location === point);
      const safe = record?.condition === 'Aman';
      return <div key={point} style={{ position: 'relative', display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr)', gap: 10, paddingBottom: index === points.length - 1 ? 0 : 14 }}>
        {index < points.length - 1 && <div style={{ position: 'absolute', left: 14, top: 28, bottom: 0, width: 2, background: record ? '#99f6e4' : '#e2e8f0' }} />}
        <div style={{ position: 'relative', zIndex: 1, width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', background: record ? (safe ? '#0d9488' : '#ef4444') : '#fff', color: record ? '#fff' : '#94a3b8', border: `2px solid ${record ? (safe ? '#0d9488' : '#ef4444') : '#cbd5e1'}`, fontSize: 10, fontWeight: 900 }}>{record ? '✓' : index + 1}</div>
        <div style={{ padding: 12, borderRadius: 14, background: record ? (safe ? '#f0fdfa' : '#fef2f2') : '#f8fafc', border: `1px solid ${record ? (safe ? '#99f6e4' : '#fecaca') : '#e2e8f0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ flex: 1, color: '#1e293b', fontSize: 12, fontWeight: 900 }}>{point}</div>{record ? <span style={{ color: safe ? '#047857' : '#b91c1c', fontSize: 10, fontWeight: 900 }}>{record.condition}</span> : onCheck ? <button type="button" onClick={() => onCheck(point)} style={{ border: 0, borderRadius: 9, padding: '7px 10px', background: '#2563eb', color: '#fff', fontSize: 10, fontWeight: 800 }}>Cek Titik</button> : <span style={{ color: '#94a3b8', fontSize: 10 }}>Belum diperiksa</span>}</div>
          {record && <div style={{ marginTop: 5, color: '#64748b', fontSize: 10 }}>{record.patrol_time?.slice(0, 5)} · {record.petugas}{record.accuracy ? ` · GPS ±${Math.round(Number(record.accuracy))} m` : ''}</div>}
          {record && (record.findings || record.action) && <div style={{ marginTop: 6, color: '#475569', fontSize: 10 }}>{record.findings}{record.action ? ` · Tindakan: ${record.action}` : ''}</div>}
          {record && record.latitude && record.longitude && <details style={{ marginTop: 8 }}><summary style={{ color: '#2563eb', fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>Lihat lokasi GPS</summary><LocationMap lat={Number(record.latitude)} lng={Number(record.longitude)} title={`Titik Patroli · ${point}`} address={`${point}${record.accuracy ? ` · Akurasi ±${Math.round(Number(record.accuracy))} m` : ''}`} height={140} zoom={17} /></details>}
          {record?.photo_url && <img src={resolveSlipMediaUrl(record.photo_url)} alt={`Temuan ${point}`} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginTop: 8 }} />}
        </div>
      </div>;
    })}
  </div>
);

const SubmitButton = ({ label, disabled = false, icon: Icon = Plus }) => <button type="submit" disabled={disabled} style={{ padding: 14, border: 'none', borderRadius: 15, background: disabled ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 7, boxShadow: '0 4px 14px rgba(37,99,235,.25)', cursor: disabled ? 'wait' : 'pointer' }}><Icon size={16} />{label}</button>;

const LogCard = ({ log, onDelete, onComplete, onAddSuratJalan, onPreviewImage, onDeleteSuratJalan }) => {
  const isVehicle = log.type === 'vehicle';
  const hasMasuk = Boolean(log.masuk && String(log.masuk).trim() !== '');
  const hasKeluar = Boolean(log.keluar && String(log.keluar).trim() !== '');
  const isComplete = isVehicle && hasMasuk && hasKeluar;
  const masukTime = String(log.masuk || '').replace('-', ':');
  const keluarTime = String(log.keluar || '').replace('-', ':');
  const [isExpandedSj, setIsExpandedSj] = useState(true);

  let statusLabel = '';
  let badgeBackground = '#eff6ff';
  let badgeColor = '#1d4ed8';
  if (!isVehicle) {
    if (log.type === 'patrol') { statusLabel = `Putaran ${log.round}`; }
    else { statusLabel = TABS.find(tab => tab.key === log.type)?.label || 'Catatan'; }
  }

  const title = isVehicle
    ? `${log.nopol || log.plate || 'Tanpa Nopol'}`
    : log.type === 'patrol' ? `Patroli · ${log.location}`
    : log.type === 'checklist' ? `Checklist · ${log.nopol}`
    : log.activity_type;

  const suratJalanList = isVehicle ? (log.surat_jalan || log.suratJalan || []) : [];

  const formattedDate = log.tanggal
    ? new Date(`${log.tanggal}T00:00:00`).toLocaleDateString('id-ID', { dateStyle: 'medium' })
    : (log.timestamp ? new Date(log.timestamp).toLocaleDateString('id-ID', { dateStyle: 'medium' }) : '-');

  return (
    <article style={{ background: '#fff', borderRadius: 22, border: '1px solid #e2e8f0', marginBottom: 16, overflow: 'hidden', boxShadow: '0 4px 18px rgba(15,23,42,.06)' }}>
      <div style={{ padding: '16px 16px 12px' }}>
        {/* Header: Foto + Identitas + Status Badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {/* Foto Thumbnail (klik untuk preview) */}
            <div
              onClick={() => { if (log.photo_url && onPreviewImage) onPreviewImage({ url: resolveSlipMediaUrl(log.photo_url), title: `${title} · ${[log.jenis, log.kode].filter(Boolean).join(' ') || 'Armada'}` }); }}
              title={log.photo_url ? 'Klik untuk memperbesar gambar' : undefined}
              style={{ width: 60, height: 60, borderRadius: 14, background: '#edf5fb', overflow: 'hidden', flexShrink: 0, position: 'relative', border: '1px solid #e2e8f0', cursor: log.photo_url ? 'pointer' : 'default' }}
            >
              {log.photo_url ? (
                <>
                  <img src={resolveSlipMediaUrl(log.photo_url)} alt="Foto kendaraan" onError={event => { event.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', right: 3, bottom: 3, background: 'rgba(15,23,42,.75)', borderRadius: 6, padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ZoomIn size={11} color="#fff" />
                  </div>
                </>
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: '#38a1f3' }}><CarFront size={28} strokeWidth={1.8} /></div>
              )}
            </div>
            {/* Identitas + Tanggal */}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#0f172a', fontSize: 17, lineHeight: 1.25, fontWeight: 900, letterSpacing: '.02em', overflowWrap: 'anywhere' }}>{title}</div>
              <div style={{ marginTop: 3, color: '#64748b', fontSize: 11, fontWeight: 600 }}>
                {isVehicle ? [log.jenis, log.kode].filter(Boolean).join(' · ') || 'Armada Operasional' : (log.activity_type || 'Aktivitas Security')}
              </div>
              <div style={{ marginTop: 3, color: '#94a3b8', fontSize: 10, fontWeight: 600 }}>{formattedDate}</div>
            </div>
          </div>
          {/* Status Badge */}
          <div>
            {isVehicle ? (
              isComplete ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 999, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>✓ Selesai</span>
              : hasMasuk ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 999, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>⏳ Belum Keluar</span>
              : hasKeluar ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 999, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>⏳ Belum Masuk</span>
              : <span style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 999, background: '#f1f5f9', color: '#475569', fontSize: 10, fontWeight: 800 }}>{log.direction}</span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 999, background: badgeBackground, color: badgeColor, fontSize: 10, fontWeight: 800 }}>{statusLabel}</span>
            )}
          </div>
        </div>

        {/* 2 Box Simetris: Jam Masuk & Keluar */}
        {isVehicle && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
            <div style={{ padding: '10px 12px', borderRadius: 14, background: hasMasuk ? '#f0fdf4' : '#f8fafc', border: `1px solid ${hasMasuk ? '#bbf7d0' : '#e2e8f0'}`, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 10, fontWeight: 800, color: hasMasuk ? '#15803d' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                <LogIn size={12} /><span>Jam Masuk</span>
              </div>
              <div style={{ marginTop: 4, fontSize: 15, fontWeight: 900, color: hasMasuk ? '#0f172a' : '#94a3b8' }}>{hasMasuk ? `${masukTime} WIB` : '—'}</div>
            </div>
            <div style={{ padding: '10px 12px', borderRadius: 14, background: hasKeluar ? '#fef2f2' : '#f8fafc', border: `1px solid ${hasKeluar ? '#fecaca' : '#e2e8f0'}`, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 10, fontWeight: 800, color: hasKeluar ? '#b91c1c' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                <LogOut size={12} /><span>Jam Keluar</span>
              </div>
              <div style={{ marginTop: 4, fontSize: 15, fontWeight: 900, color: hasKeluar ? '#0f172a' : '#94a3b8' }}>{hasKeluar ? `${keluarTime} WIB` : '—'}</div>
            </div>
          </div>
        )}

        {log.type === 'patrol' && log.latitude && log.longitude && Number.isFinite(Number(log.latitude)) && Number.isFinite(Number(log.longitude)) && (
          <div style={{ marginTop: 12 }}><LocationMap lat={Number(log.latitude)} lng={Number(log.longitude)} title={`Titik Patroli · ${log.location}`} address={`${log.location}${log.accuracy ? ` · Akurasi ±${Math.round(Number(log.accuracy))} m` : ''}`} height={150} zoom={17} /></div>
        )}

        {/* Info Sopir & Petugas */}
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Sopir / Driver</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginTop: 2, overflowWrap: 'anywhere' }}>
              {isVehicle ? (log.driver || 'Tidak ada data') : log.type === 'patrol' ? log.condition : title}
            </div>
          </div>
          <div style={{ minWidth: 0, textAlign: 'right' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Petugas Security</div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', marginTop: 2, overflowWrap: 'anywhere' }}>{log.officer_name || log.petugas}</div>
          </div>
        </div>

        {(log.keterangan || log.findings) && (
          <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontSize: 11, lineHeight: 1.4 }}>
            <span style={{ fontWeight: 800, color: '#334155' }}>Catatan: </span>{log.keterangan || log.findings}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 12 }}>
          {isVehicle && !isComplete ? (
            <button type="button" onClick={() => onComplete(log)} style={{ flex: 1, border: 0, borderRadius: 10, padding: '9px 14px', background: log.masuk ? '#fff7ed' : '#eff6ff', color: log.masuk ? '#c2410c' : '#1d4ed8', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
              Lengkapi {log.masuk ? 'Keluar' : 'Masuk'}
            </button>
          ) : isVehicle && isComplete ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, background: '#ecfdf5', color: '#047857', fontSize: 11, fontWeight: 800, border: '1px solid #a7f3d0' }}>✓ Lengkap</span>
          ) : null}
          <button type="button" onClick={() => onDelete(log.id)} aria-label="Hapus catatan" style={{ marginLeft: 'auto', width: 38, height: 38, border: 'none', background: '#fef2f2', color: '#ef4444', borderRadius: 10, display: 'grid', placeItems: 'center', cursor: 'pointer' }}><Trash2 size={16} /></button>
        </div>
      </div>

      {/* Surat Jalan Accordion (hanya kendaraan) */}
      {isVehicle && (
        <div style={{ borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px' }}>
            <button type="button" onClick={() => setIsExpandedSj(v => !v)} style={{ border: 0, background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', textAlign: 'left' }}>
              <FileText size={15} color="#2563eb" />
              <span style={{ fontSize: 12, fontWeight: 900, color: '#1e293b' }}>Surat Jalan ({suratJalanList.length})</span>
              {isExpandedSj ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
            </button>
            <button type="button" onClick={() => onAddSuratJalan && onAddSuratJalan(log)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, background: '#2563eb', color: '#fff', border: 0, fontSize: 10, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,.2)' }}>
              <Plus size={12} strokeWidth={2.5} />
              {suratJalanList.length === 0 ? 'Lengkapi Surat Jalan' : 'Tambah SJ'}
            </button>
          </div>
          {isExpandedSj && (
            <div style={{ padding: '0 16px 14px' }}>
              {suratJalanList.length === 0 ? (
                <div style={{ padding: '12px 14px', borderRadius: 12, background: '#fff', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 8px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Belum ada dokumen surat jalan untuk armada ini.</p>
                  <button type="button" onClick={() => onAddSuratJalan && onAddSuratJalan(log)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 9, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                    <Plus size={13} strokeWidth={2.5} /> Lengkapi Surat Jalan
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {suratJalanList.map((sj, idx) => {
                    const direction = resolveSuratJalanDirection(sj, log.direction || (log.keluar ? 'Keluar' : 'Masuk'));
                    const isMasuk = direction.toLowerCase() === 'masuk';
                    return (
                      <div key={sj.id || idx} style={{ padding: '10px 12px', borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,.02)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 900, background: isMasuk ? '#ecfdf5' : '#fef2f2', color: isMasuk ? '#047857' : '#b91c1c', border: `1px solid ${isMasuk ? '#a7f3d0' : '#fecaca'}` }}>
                            {isMasuk ? <LogIn size={11} /> : <LogOut size={11} />}
                            SJ {direction}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <strong style={{ fontSize: 12, color: '#0f172a', letterSpacing: '.02em' }}>{sj.no_surat_jalan}</strong>
                            {onDeleteSuratJalan && (
                              <button type="button" title="Hapus Surat Jalan" onClick={() => onDeleteSuratJalan(sj.id, log.id)} style={{ border: 0, background: '#fee2e2', color: '#dc2626', width: 24, height: 24, borderRadius: 6, display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#475569', background: '#f8fafc', padding: '5px 8px', borderRadius: 8 }}>
                          <span>⚖️ Tonase: <strong style={{ color: '#0f172a' }}>{Number(sj.tonase || 0).toLocaleString('id-ID')} Kg</strong></span>
                          <span>📦 Qty: <strong style={{ color: '#0f172a' }}>{Number(sj.qty || 0).toLocaleString('id-ID')} Koli</strong></span>
                        </div>
                        {sj.keterangan && <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4, fontStyle: 'italic' }}>Catatan: {sj.keterangan}</div>}
                      </div>
                    );
                  })}
                  {suratJalanList.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '8px 10px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                      <div><div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL TONASE</div><div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>{suratJalanList.reduce((a, c) => a + (parseFloat(c.tonase) || 0), 0).toLocaleString('id-ID')} Kg</div></div>
                      <div><div style={{ fontSize: 9, color: '#1e40af', fontWeight: 800 }}>TOTAL QTY</div><div style={{ fontSize: 12, fontWeight: 900, color: '#1e3a8a' }}>{suratJalanList.reduce((a, c) => a + (parseInt(c.qty, 10) || 0), 0).toLocaleString('id-ID')} Koli</div></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
};
